/**
 * Where a Keycloak round trip comes back to.
 *
 * The sign-in redirect leaves the app entirely, so the page being left has to
 * travel with it: `signIn()` puts it in the OIDC `state` — kept in the state
 * store, not the URL — and `/callback` reads it back. Without that the callback
 * has nothing to go on but "/", which is what made a shared-chat link open the
 * app's own landing page for a recipient who had to sign in to read it.
 */

// Paths that exist only to service the auth round trip. Returning to one would
// re-enter the callback with a spent code, or load the silent-renew shim — an
// iframe document — as a real page.
const AUTH_PATHS = ['/callback', '/silent-renew'];

/** The page currently on screen, as a path this app can navigate back to. */
export function currentReturnTo(loc = window.location) {
  return `${loc.pathname}${loc.search}${loc.hash}`;
}

/**
 * A return path is only usable if it points back into this app.
 *
 * `state` is our own value, but treating it as trusted input is how an open
 * redirect starts, so anything that is not a single-slash-rooted path is
 * discarded rather than repaired: `//evil.test` and `https://evil.test` are both
 * a different origin to a browser, and `navigate()` honours them.
 */
export function safeReturnTo(raw) {
  if (typeof raw !== 'string' || !raw.startsWith('/')) return '/';
  if (raw.startsWith('//') || raw.startsWith('/\\')) return '/';
  const [path] = raw.split(/[?#]/);
  if (AUTH_PATHS.includes(path)) return '/';
  return raw;
}
